import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Lock, RefreshCw, Calendar, ChevronLeft, ChevronRight, Download, Activity } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ACCESS_CODE = "ExcelViewer2026";
const SS_KEY = "bc_excel_viewer_unlocked";

// ─── BetCracker-matched dark palette ─────────────────────────────────────────
const T = {
  bg:         "#070709",
  panel:      "#0E0E12",
  panelAlt:   "#14141A",
  panelHover: "rgba(34,255,163,0.05)",
  hairline:   "var(--bc-border, rgba(255,255,255,0.07))",
  hairline2:  "var(--bc-border, rgba(255,255,255,0.1))",
  ink:        "#F5F5F4",
  muted:      "#6B6B73",
  sub:        "#9A9A9E",
  neon:       "#22FFA3",
  neonDim:    "rgba(34,255,163,0.12)",
  neonGlow:   "rgba(34,255,163,0.55)",
  cyan:       "#9DF6FF",
  amber:      "#F59E0B",
  amberDim:   "rgba(245,158,11,0.12)",
  danger:     "#FF2E3E",
};

function todayISO() {
  return new Date().toISOString().slice(0, 10);
}

function shiftDate(iso, days) {
  const d = new Date(iso + "T12:00:00");
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
}

// ─── Columns definition ─────────────────────────────────────────────────────
const COLUMNS = [
  { key: "date",         label: "Data",             width:  84, letter: "A" },
  { key: "time",         label: "Orario",           width:  62, letter: "B" },
  { key: "league",       label: "Campionato",       width: 170, letter: "C" },
  { key: "home",         label: "Squadra casa",     width: 158, letter: "D" },
  { key: "away",         label: "Squadra ospite",   width: 158, letter: "E" },
  { key: "corners_home",   label: "Corner casa",      width:  66, letter: "F", align: "right",  mono: true, accent: T.cyan },
  { key: "corners_away",   label: "Corner ospite",    width:  66, letter: "G", align: "right",  mono: true, accent: T.cyan },
  { key: "actual_corners", label: "Esito corner",     width:  78, letter: "H", align: "center", mono: true, accent: T.neon },
  { key: "cards_home",     label: "Cart. casa",       width:  70, letter: "I", align: "right",  mono: true, accent: T.amber },
  { key: "cards_away",     label: "Cart. ospite",     width:  70, letter: "J", align: "right",  mono: true, accent: T.amber },
  { key: "p1",             label: "1",                width:  54, letter: "K", align: "right",  mono: true, accent: T.cyan,  pct: true },
  { key: "px",             label: "X",                width:  54, letter: "L", align: "right",  mono: true, accent: T.cyan,  pct: true },
  { key: "p2",             label: "2",                width:  54, letter: "M", align: "right",  mono: true, accent: T.cyan,  pct: true },
  { key: "dc",             label: "DC",               width: 170, letter: "N", align: "center", accent: T.cyan, big: true },
  { key: "actual_score",   label: "Esito gara",       width:  74, letter: "O", align: "center", mono: true, accent: T.neon },
  { key: "ou",             label: "U/O",              width: 170, letter: "P", align: "center", accent: T.neon, big: true },
  { key: "over25",         label: "Over 2.5",         width:  68, letter: "Q", align: "right",  mono: true, accent: T.neon,  pct: true },
  { key: "under25",        label: "Under 2.5",        width:  72, letter: "R", align: "right",  mono: true, accent: T.neon,  pct: true },
  { key: "gol",            label: "Gol",              width:  58, letter: "S", align: "right",  mono: true, accent: T.amber, pct: true },
  { key: "nogol",          label: "NoGol",            width:  64, letter: "T", align: "right",  mono: true, accent: T.amber, pct: true },
];

const PRED_KEYS = new Set([
  "corners_home", "corners_away", "actual_corners", "actual_score",
  "cards_home", "cards_away", "p1", "px", "p2", "dc", "ou",
  "over25", "under25", "gol", "nogol",
]);

// ─── CSV export ─────────────────────────────────────────────────────────────
function toCSV(rows) {
  const esc = (v) => {
    if (v === null || v === undefined) return "";
    const s = String(v);
    return /[",\n;]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const header = COLUMNS.map((c) => esc(c.label)).join(";");
  const body = rows.map((r) =>
    COLUMNS.map((c) => {
      const v = r[c.key];
      if (v === null || v === undefined || v === "") return "";
      return esc(c.pct ? `${v}%` : v);
    }).join(";"),
  ).join("\n");
  return header + "\n" + body;
}

function downloadCSV(rows, date) {
  const csv = toCSV(rows);
  const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `excel-viewer_${date}.csv`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

// ─── Access gate (dark BetCracker style) ────────────────────────────────────

function AccessForm({ onSuccess }) {
  const [code, setCode] = useState("");
  const [err, setErr] = useState(null);

  const submit = (e) => {
    e.preventDefault();
    if (code.trim() === ACCESS_CODE) {
      sessionStorage.setItem(SS_KEY, "1");
      setErr(null);
      onSuccess();
    } else {
      setErr("Codice non valido.");
    }
  };

  return (
    <div
      data-testid="excel-access-gate"
      style={{
        minHeight: "100vh", display: "flex", alignItems: "center",
        justifyContent: "center",
        background: T.bg,
        color: T.ink, padding: "2rem",
        position: "relative", overflow: "hidden",
      }}
    >
      {/* soft green aura + top line */}
      <div aria-hidden style={{
        position: "absolute", inset: 0, zIndex: 0,
        background: `radial-gradient(circle at 50% 30%, ${T.neonDim}, transparent 55%)`,
      }} />
      <div aria-hidden style={{
        position: "absolute", top: 0, left: 0, right: 0, height: 1,
        background: `linear-gradient(to right, transparent, ${T.neon}, transparent)`,
        boxShadow: `0 0 14px ${T.neonGlow}`,
      }} />

      <form
        onSubmit={submit}
        style={{
          position: "relative", zIndex: 1,
          width: "100%", maxWidth: 420,
          background: T.panel,
          border: `1px solid ${T.hairline2}`,
          borderTop: `2px solid ${T.neon}`,
          boxShadow: `0 -8px 32px -10px ${T.neonGlow}`,
          padding: "2.25rem 2rem",
        }}
      >
        <div style={{ textAlign: "center", marginBottom: "1.75rem" }}>
          <div style={{
            width: 44, height: 44, margin: "0 auto 0.85rem",
            display: "flex", alignItems: "center", justifyContent: "center",
            background: T.neonDim,
            border: `1px solid ${T.neon}60`,
            boxShadow: `0 0 16px ${T.neonGlow}, inset 0 0 10px ${T.neonDim}`,
          }}>
            <Lock size={16} style={{ color: T.neon, filter: `drop-shadow(0 0 6px ${T.neon})` }} />
          </div>
          <div style={{
            fontFamily: "'Barlow Condensed',sans-serif", fontWeight: 700,
            fontSize: "0.65rem", letterSpacing: "0.35em", color: T.neon,
            textTransform: "uppercase", marginBottom: 6,
            textShadow: `0 0 8px ${T.neonGlow}`,
          }}>
            Bet Cracker · Data Export
          </div>
          <h1 style={{
            fontFamily: "'Barlow Condensed',sans-serif", fontSize: "2rem",
            fontWeight: 900, color: T.ink, margin: 0, letterSpacing: "-0.01em",
          }}>
            Excel Viewer
          </h1>
          <p style={{
            fontFamily: "'DM Sans',sans-serif", fontSize: "0.85rem",
            color: T.sub, margin: "0.4rem 0 0",
          }}>
            Inserisci il codice di accesso per visualizzare i dati.
          </p>
        </div>

        <label style={{
          display: "block",
          fontFamily: "'Barlow Condensed',sans-serif",
          fontSize: "0.65rem", letterSpacing: "0.25em", textTransform: "uppercase",
          fontWeight: 700, color: T.muted, marginBottom: 8,
        }}>
          Codice d'accesso
        </label>
        <input
          data-testid="excel-code-input"
          autoFocus
          autoComplete="off"
          spellCheck={false}
          type="password"
          value={code}
          onChange={(e) => setCode(e.target.value)}
          placeholder=""
          style={{
            width: "100%", padding: "0.75rem 0.9rem",
            background: T.bg, border: `1px solid ${T.hairline2}`,
            color: T.ink, outline: "none",
            fontFamily: "'JetBrains Mono',monospace", fontSize: "0.95rem",
            letterSpacing: "0.1em",
          }}
          onFocus={(e) => { e.currentTarget.style.borderColor = T.neon; e.currentTarget.style.boxShadow = `0 0 12px ${T.neonGlow}`; }}
          onBlur={(e) => { e.currentTarget.style.borderColor = T.hairline2; e.currentTarget.style.boxShadow = "none"; }}
        />
        {err && (
          <div
            data-testid="excel-access-error"
            style={{
              marginTop: 12, padding: "0.6rem 0.85rem",
              background: "rgba(255,46,62,0.08)",
              border: `1px solid ${T.danger}55`,
              color: T.danger,
              fontFamily: "'JetBrains Mono',monospace", fontSize: "0.78rem",
              letterSpacing: "0.05em",
            }}
          >
            {err}
          </div>
        )}

        <button
          data-testid="excel-submit"
          type="submit"
          style={{
            marginTop: "1.25rem", width: "100%",
            padding: "0.8rem",
            background: `linear-gradient(180deg, ${T.neonDim} 0%, rgba(34,255,163,0.04) 100%)`,
            color: T.ink, border: `1px solid ${T.neon}80`,
            boxShadow: `0 0 16px ${T.neonGlow}, inset 0 0 14px ${T.neonDim}`,
            cursor: "pointer",
            fontFamily: "'Barlow Condensed',sans-serif", fontWeight: 900,
            fontSize: "0.95rem", letterSpacing: "0.35em", textTransform: "uppercase",
            textShadow: `0 0 10px ${T.neonGlow}`,
          }}
        >
          Accedi
        </button>
      </form>
    </div>
  );
}

// ─── Main Excel-style table ─────────────────────────────────────────────────

function ExcelTable() {
  const [date, setDate] = useState(todayISO());
  const [rows, setRows] = useState([]);
  const [stats, setStats] = useState({ total: 0, cached: 0, pending: 0 });
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [refreshingResults, setRefreshingResults] = useState(false);
  const [refreshToast, setRefreshToast] = useState(null);

  const fetchData = (d = date) => {
    setLoading(true);
    setErr(null);
    axios
      .get(`${API}/excel-viewer/data`, { params: { code: ACCESS_CODE, date: d } })
      .then((r) => {
        setRows(r.data?.rows || []);
        setStats({
          total:   r.data?.total   || 0,
          cached:  r.data?.cached  || 0,
          pending: r.data?.pending || 0,
        });
      })
      .catch((e) => setErr(e?.response?.data?.detail || "Errore nel caricamento dei dati."))
      .finally(() => setLoading(false));
  };

  const refreshResults = async () => {
    setRefreshingResults(true);
    setRefreshToast(null);
    try {
      const res = await axios.post(`${API}/excel-viewer/refresh-results`, null, {
        params: { code: ACCESS_CODE, date },
      });
      setRefreshToast({
        kind: (res.data?.updated || 0) > 0 ? "ok" : "warn",
        msg: res.data?.message || "Aggiornamento completato.",
      });
      // Reload the table so the new actuals show up
      fetchData(date);
    } catch (e) {
      setRefreshToast({
        kind: "err",
        msg: e?.response?.data?.detail || "Errore durante l'aggiornamento.",
      });
    } finally {
      setRefreshingResults(false);
      setTimeout(() => setRefreshToast(null), 5000);
    }
  };

  useEffect(() => { fetchData(date); /* eslint-disable-line */ }, [date]);

  const leagueGroups = useMemo(() => {
    const groups = [];
    let current = null;
    let lineNo = 1;
    rows.forEach((r) => {
      const key = `${r.country}||${r.league}`;
      if (!current || current.key !== key) {
        current = { key, country: r.country, league: r.league, rows: [] };
        groups.push(current);
      }
      current.rows.push({ ...r, _lineNo: lineNo });
      lineNo += 1;
    });
    return groups;
  }, [rows]);

  const missingCardsCount = useMemo(
    () => rows.filter((r) => r.cards_home == null || r.cards_away == null).length,
    [rows],
  );

  return (
    <div
      data-testid="excel-viewer-page"
      style={{
        minHeight: "100vh", background: T.bg, color: T.ink,
        fontFamily: "'DM Sans',sans-serif",
        position: "relative",
      }}
    >
      {/* top neon hairline */}
      <div aria-hidden style={{
        position: "fixed", top: 0, left: 0, right: 0, height: 1,
        background: `linear-gradient(to right, transparent, ${T.neon}, transparent)`,
        boxShadow: `0 0 14px ${T.neonGlow}`, zIndex: 3,
      }} />

      {/* Top bar */}
      <div
        style={{
          display: "flex", alignItems: "center", gap: "0.75rem",
          padding: "0.85rem 1.25rem",
          background: T.panel,
          borderBottom: `1px solid ${T.hairline2}`,
          boxShadow: `0 8px 24px -14px rgba(0,0,0,0.8)`,
          position: "sticky", top: 0, zIndex: 2,
        }}
      >
        <div style={{
          width: 32, height: 32,
          display: "flex", alignItems: "center", justifyContent: "center",
          background: T.neonDim, border: `1px solid ${T.neon}60`,
          boxShadow: `0 0 14px ${T.neonGlow}, inset 0 0 8px ${T.neonDim}`,
        }}>
          <Activity size={15} style={{ color: T.neon, filter: `drop-shadow(0 0 6px ${T.neon})` }} />
        </div>
        <div style={{ lineHeight: 1.1 }}>
          <div style={{
            fontFamily: "'Barlow Condensed',sans-serif", fontWeight: 900,
            fontSize: "1rem", letterSpacing: "0.02em", color: T.ink,
          }}>
            BET CRACKER <span style={{ color: T.neon, textShadow: `0 0 10px ${T.neonGlow}` }}>· EXCEL VIEWER</span>
          </div>
          <div style={{
            fontFamily: "'JetBrains Mono',monospace",
            fontSize: "0.66rem", letterSpacing: "0.12em",
            color: T.sub, textTransform: "uppercase",
            marginTop: 3,
          }}>
            {stats.total} partite · {stats.cached} pronte · {stats.pending} in attesa
          </div>
        </div>

        {/* Date picker */}
        <div style={{
          marginLeft: "auto", display: "flex", alignItems: "stretch",
          background: T.bg, border: `1px solid ${T.hairline2}`,
        }}>
          <button
            data-testid="excel-date-prev"
            onClick={() => setDate((d) => shiftDate(d, -1))}
            style={{ padding: "0 0.7rem", background: "transparent", border: "none",
              borderRight: `1px solid ${T.hairline}`, color: T.neon, cursor: "pointer" }}
          >
            <ChevronLeft size={15} />
          </button>
          <label style={{
            display: "flex", alignItems: "center", gap: "0.45rem",
            padding: "0.5rem 0.9rem", cursor: "pointer",
            fontFamily: "'JetBrains Mono',monospace", fontSize: "0.8rem",
            color: T.ink, position: "relative",
          }}>
            <Calendar size={13} style={{ color: T.neon }} />
            <span>{date}</span>
            <input
              data-testid="excel-date-input"
              type="date"
              value={date}
              onChange={(e) => e.target.value && setDate(e.target.value)}
              style={{
                position: "absolute", inset: 0, opacity: 0, cursor: "pointer",
                border: "none", background: "transparent",
              }}
            />
          </label>
          <button
            data-testid="excel-date-next"
            onClick={() => setDate((d) => shiftDate(d, 1))}
            style={{ padding: "0 0.7rem", background: "transparent", border: "none",
              borderLeft: `1px solid ${T.hairline}`, color: T.neon, cursor: "pointer" }}
          >
            <ChevronRight size={15} />
          </button>
        </div>

        <button
          data-testid="excel-refresh"
          onClick={() => fetchData(date)}
          disabled={loading}
          style={{
            padding: "0.5rem 0.85rem", background: "transparent",
            border: `1px solid ${T.hairline2}`, color: T.sub,
            cursor: loading ? "wait" : "pointer",
            fontFamily: "'Barlow Condensed',sans-serif", fontWeight: 700,
            fontSize: "0.72rem", letterSpacing: "0.22em", textTransform: "uppercase",
            display: "inline-flex", alignItems: "center", gap: "0.4rem",
          }}
        >
          <RefreshCw size={12} style={{ animation: loading ? "spin 0.8s linear infinite" : "none" }} />
          Aggiorna
        </button>

        <button
          data-testid="excel-refresh-results"
          onClick={refreshResults}
          disabled={refreshingResults}
          title="Aggiorna esiti finali (corner + risultato) per le partite concluse"
          style={{
            padding: "0.5rem 0.85rem", background: T.neonDim,
            border: `1px solid ${T.neon}80`, color: T.neon,
            cursor: refreshingResults ? "wait" : "pointer",
            fontFamily: "'Barlow Condensed',sans-serif", fontWeight: 700,
            fontSize: "0.72rem", letterSpacing: "0.22em", textTransform: "uppercase",
            display: "inline-flex", alignItems: "center", gap: "0.4rem",
            boxShadow: `0 0 12px -2px ${T.neonGlow}`,
          }}
        >
          <Activity size={12} style={{ animation: refreshingResults ? "spin 0.8s linear infinite" : "none" }} />
          Aggiorna esiti
        </button>

        <button
          data-testid="excel-export"
          onClick={() => downloadCSV(rows, date)}
          disabled={!rows.length}
          style={{
            padding: "0.5rem 0.95rem",
            background: rows.length
              ? `linear-gradient(180deg, ${T.neonDim} 0%, rgba(34,255,163,0.04) 100%)`
              : "transparent",
            color: rows.length ? T.ink : T.muted,
            border: `1px solid ${rows.length ? T.neon + "80" : T.hairline2}`,
            boxShadow: rows.length ? `0 0 12px ${T.neonGlow}` : "none",
            cursor: rows.length ? "pointer" : "not-allowed",
            fontFamily: "'Barlow Condensed',sans-serif", fontWeight: 900,
            fontSize: "0.72rem", letterSpacing: "0.28em", textTransform: "uppercase",
            display: "inline-flex", alignItems: "center", gap: "0.4rem",
            textShadow: rows.length ? `0 0 8px ${T.neonGlow}` : "none",
          }}
        >
          <Download size={12} />
          Esporta CSV
        </button>
      </div>

      {/* Content */}
      <div style={{ padding: "1rem 1.25rem" }}>
        {refreshToast && (
          <div
            data-testid="excel-refresh-toast"
            style={{
              padding: "0.6rem 1rem", marginBottom: "0.75rem",
              fontFamily: "'JetBrains Mono',monospace", fontSize: "0.78rem",
              background:
                refreshToast.kind === "ok"   ? "rgba(34,255,163,0.08)" :
                refreshToast.kind === "err"  ? "rgba(255,46,62,0.08)" :
                "rgba(245,158,11,0.08)",
              border: `1px solid ${
                refreshToast.kind === "ok"   ? T.neon + "55" :
                refreshToast.kind === "err"  ? T.danger + "55" :
                T.amber + "55"}`,
              color:
                refreshToast.kind === "ok"   ? T.neon :
                refreshToast.kind === "err"  ? T.danger :
                T.amber,
            }}
          >
            {refreshToast.msg}
          </div>
        )}
        {err ? (
          <div
            style={{
              padding: "0.75rem 1rem", background: "rgba(255,46,62,0.08)",
              border: `1px solid ${T.danger}55`, color: T.danger,
              fontFamily: "'JetBrains Mono',monospace", fontSize: "0.8rem",
              marginBottom: "0.75rem",
            }}
          >
            {err}
          </div>
        ) : null}

        <div
          style={{
            background: T.panel, border: `1px solid ${T.hairline2}`,
            overflow: "auto",
            boxShadow: `0 8px 24px -18px rgba(0,0,0,0.6)`,
          }}
        >
          <table
            data-testid="excel-viewer-table"
            style={{
              width: "100%", borderCollapse: "collapse",
              fontSize: "0.82rem", color: T.ink,
            }}
          >
            <thead>
              <tr style={{ background: T.panelAlt }}>
                <th style={headerCellLetterStyle(34)}></th>
                {COLUMNS.map((c) => (
                  <th key={`letter-${c.key}`} style={{ ...headerCellLetterStyle(c.width) }}>
                    {c.letter}
                  </th>
                ))}
              </tr>
              <tr>
                <th style={rowNumberStyle(true)}>1</th>
                {COLUMNS.map((c) => (
                  <th
                    key={c.key}
                    style={{
                      ...cellBase(c.width),
                      background: T.panel,
                      borderBottom: `1px solid ${T.hairline2}`,
                      fontFamily: "'Barlow Condensed',sans-serif",
                      fontWeight: 800, color: T.cyan,
                      fontSize: "0.78rem", letterSpacing: "0.18em",
                      textTransform: "uppercase",
                      textShadow: `0 0 8px rgba(157,246,255,0.45)`,
                      textAlign: c.align || "left",
                      position: "sticky", top: 0,
                      padding: "0.55rem 0.5rem",
                    }}
                  >
                    {c.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={COLUMNS.length + 1} style={{
                    padding: "2.5rem", textAlign: "center", color: T.muted,
                    fontFamily: "'Barlow Condensed',sans-serif",
                    letterSpacing: "0.25em", textTransform: "uppercase",
                  }}>
                    Caricamento…
                  </td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={COLUMNS.length + 1} style={{
                    padding: "2.5rem", textAlign: "center", color: T.muted,
                    fontFamily: "'Barlow Condensed',sans-serif",
                    letterSpacing: "0.2em", textTransform: "uppercase",
                  }}>
                    Nessuna partita per questa data.
                  </td>
                </tr>
              ) : (
                leagueGroups.map((g) => (
                  <LeagueGroup key={g.key} g={g} />
                ))
              )}
            </tbody>
          </table>
        </div>

        {!loading && missingCardsCount > 0 && (
          <div style={{
            marginTop: "0.75rem",
            padding: "0.6rem 0.95rem",
            background: T.amberDim,
            border: `1px solid ${T.amber}50`,
            fontFamily: "'JetBrains Mono',monospace",
            fontSize: "0.72rem", letterSpacing: "0.04em",
            color: T.amber,
          }}>
            Cartellini mancanti su {missingCardsCount} partite — si popoleranno
            automaticamente al prossimo prefetch (07:00) o dopo l'apertura delle
            partite nell'app principale.
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Reusable subcomponents ─────────────────────────────────────────────────

function LeagueGroup({ g }) {
  return (
    <>
      <tr>
        <td style={rowNumberStyle(false)}></td>
        <td
          colSpan={COLUMNS.length}
          style={{
            padding: "0.5rem 0.9rem",
            background: T.neonDim,
            borderBottom: `1px solid ${T.neon}35`,
            borderTop: `1px solid ${T.neon}35`,
            color: T.neon,
            fontFamily: "'Barlow Condensed',sans-serif",
            fontWeight: 800, fontSize: "0.72rem",
            letterSpacing: "0.25em", textTransform: "uppercase",
            textShadow: `0 0 8px ${T.neonGlow}`,
          }}
        >
          <span style={{
            display: "inline-block", width: 6, height: 6,
            background: T.neon, marginRight: "0.75rem",
            boxShadow: `0 0 8px ${T.neon}`,
          }} />
          {g.country ? `${g.country} · ` : ""}{g.league || "—"}
        </td>
      </tr>
      {g.rows.map((r) => (
        <ExcelRow key={r.fixture_id} r={r} />
      ))}
    </>
  );
}

function ExcelRow({ r }) {
  const [hover, setHover] = useState(false);
  const bg = hover
    ? T.panelHover
    : (r._lineNo % 2 === 0 ? T.panelAlt : T.panel);
  return (
    <tr
      data-testid={`excel-row-${r.fixture_id}`}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{ background: bg, transition: "background 0.1s ease" }}
    >
      <td style={rowNumberStyle(false)}>{r._lineNo + 1}</td>
      {COLUMNS.map((c) => {
        const raw = r[c.key];
        const isEmpty = raw === null || raw === undefined || raw === "";
        const isPredCol = PRED_KEYS.has(c.key);
        const hasAccent = Boolean(c.accent);
        // "Non consigliato" → muted/italic styling, no neon glow
        const isNonConsigliato = (c.key === "ou" || c.key === "dc")
          && typeof raw === "string" && raw.startsWith("N/A");
        const color = isEmpty && isPredCol
          ? T.muted
          : (isNonConsigliato ? T.muted : (hasAccent ? c.accent : T.ink));
        const fontFamily = isNonConsigliato
          ? "'DM Sans',sans-serif"
          : (c.mono ? "'JetBrains Mono',monospace" : "'DM Sans',sans-serif");
        const fontSize = isNonConsigliato
          ? "0.78rem"
          : (c.big ? "1.05rem" : (c.mono ? "0.95rem" : "0.85rem"));
        const fontWeight = isNonConsigliato ? 500
          : ((hasAccent && !isEmpty) ? 800 : 500);
        const textShadow = (hasAccent && !isEmpty && !isNonConsigliato)
          ? `0 0 10px ${c.accent}A0, 0 0 18px ${c.accent}40`
          : "none";
        const fontStyle = isNonConsigliato ? "italic" : "normal";
        const displayValue = isEmpty
          ? (isPredCol ? "—" : "")
          : (c.pct ? `${raw}%` : raw);
        return (
          <td
            key={c.key}
            style={{
              ...cellBase(c.width),
              padding: "0.55rem 0.55rem",
              textAlign: c.align || "left",
              color, fontFamily, fontSize, fontWeight, fontStyle,
              textShadow,
              letterSpacing: c.mono && !isNonConsigliato ? "0.02em" : "normal",
            }}
          >
            {displayValue}
          </td>
        );
      })}
    </tr>
  );
}

// ─── Styling helpers ────────────────────────────────────────────────────────

function rowNumberStyle(isHeader) {
  return {
    width: 34, minWidth: 34, textAlign: "center",
    background: isHeader ? T.panelAlt : T.panel,
    color: T.muted,
    fontFamily: "'JetBrains Mono',monospace", fontSize: "0.68rem",
    padding: "0.35rem 0.4rem",
    borderRight: `1px solid ${T.hairline2}`,
    borderBottom: `1px solid ${T.hairline}`,
    position: "sticky", left: 0,
  };
}

function headerCellLetterStyle(width) {
  return {
    width, minWidth: width, background: T.panelAlt,
    color: T.muted, textAlign: "center",
    padding: "0.3rem 0.4rem",
    fontFamily: "'JetBrains Mono',monospace", fontSize: "0.68rem",
    borderRight: `1px solid ${T.hairline2}`,
    borderBottom: `1px solid ${T.hairline2}`,
    fontWeight: 600, letterSpacing: "0.05em",
  };
}

function cellBase(width) {
  return {
    width, minWidth: width,
    padding: "0.5rem 0.7rem",
    borderRight: `1px solid ${T.hairline}`,
    borderBottom: `1px solid ${T.hairline}`,
    whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
  };
}

// ─── Page ───────────────────────────────────────────────────────────────────

export default function ExcelViewerPage() {
  const [unlocked, setUnlocked] = useState(() => sessionStorage.getItem(SS_KEY) === "1");

  if (!unlocked) {
    return <AccessForm onSuccess={() => setUnlocked(true)} />;
  }
  return <ExcelTable />;
}
